import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAppContext, SET_CHOSEN_PLANTS, SET_FILTERED_LIST } from '../context/AppContext';
import { useSpreadsheetContext } from '../context/SpreadsheetContext';
import { useGardenBed } from '../hooks/useGardenBed.js';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../utils/urlState';
import PlantListMobile from '../components/PlantList.mobile.jsx';
import ChoosePlantSideWindowMobile from '../components/ChoosePlantSideWindow.mobile.jsx';
import gardenBedLayouts from '../data/gardenBedLayouts.json';
import ChoosePlantPreviewSVG from '../components/ChoosePlantPreviewSVG.jsx';
import { Box, Text, Group, Button } from '@mantine/core';
import { colors, fontSize } from '../constants';

const ChoosePlantsMobile = ({ gardenBedId }) => {
  const { state, dispatch } = useAppContext();
  const { plantData } = useSpreadsheetContext();
  const { zone, style, sunlight, chosenPlants = [], bedConfig } = state.currentDesign || {};
  const { user } = state;
  const { addOrReplacePlant, plantCategories } = useGardenBed(gardenBedId);
  const [filteredPlants, setFilteredPlants] = useState([]);
  const [currentPlantCategory, setCurrentPlantCategory] = useState(null);
  const [selectedPlantId, setSelectedPlantId] = useState(null);
  const navigate = useNavigate();
  const { navigateWithParams } = useUrlState();

  // Build the initial plants array to be saved to design, ignore if already built
  useEffect(() => {
    if (chosenPlants.length === 0 && bedConfig) {
      const currentLayout = gardenBedLayouts.find((layout) => layout.id === bedConfig);
      const initialPlantsArray = currentLayout.plants.map((plant) => ({
        category: plant.category,
        plantId: null,
        quantity: plant.quantity,
      }));

      dispatch({ type: SET_CHOSEN_PLANTS, payload: initialPlantsArray });
    }
  }, [chosenPlants, bedConfig, dispatch]);

  // Get all plants that fit Zone, Style, and Sunlight
  const initialFilteredPlants = useMemo(() => {
    if (!plantData || plantData.length === 0) return [];

    return plantData.filter((plant) => {
      const zones = plant.Zone.split(',').map((r) => r.trim());
      const styles = plant.Style.split(',').map((r) => r.trim());
      const sunlightConditions = plant.Sunlight.split(',').map((r) => r.trim());

      return (
        zones.includes(zone) &&
        styles.includes(style) &&
        sunlightConditions.includes(sunlight)
      );
    });
  }, [plantData, zone, style, sunlight]);

  // Save initialFilteredPlants to AppContext whenever it changes
  useEffect(() => {
    dispatch({ type: SET_FILTERED_LIST, payload: initialFilteredPlants });
  }, [initialFilteredPlants, dispatch]);

  // Filter the previously filtered plant list to only those with the same category as is currently showing
  useEffect(() => {
    let updatedPlants = initialFilteredPlants;

    if (currentPlantCategory) {
      updatedPlants = updatedPlants.filter(
        (plant) => plant.Category === currentPlantCategory
      );
    }

    setFilteredPlants(updatedPlants);
  }, [currentPlantCategory, initialFilteredPlants]);

  // Navigate to the next screen
  const handleNext = () => {
    navigateWithParams('/summary-one');
  };

  // Navigate to the previous screen
  const handleBack = () => {
    navigate('/garden-bed');
  };

  // Handle subscription button click
  const handleSubscribe = () => {
    window.open('https://www.mylandscapedesign.com/pricing-plans', '_blank');
  };

  // Logic for choosing selected plant
  const handleSelectPlant = (plantId, category) => {
    if (plantId === null) {
      dispatch({
        type: SET_CHOSEN_PLANTS,
        payload: chosenPlants.map((p) =>
          p.category === category ? { ...p, plantId: null } : p
        ),
      });
    } else {
      addOrReplacePlant(plantId, category);
    }
    setSelectedPlantId(plantId);
  };

  return (
    <Box style={{ 
      height: '100vh', 
      width: '100vw',
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: colors.mobileBackground,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <Box style={{
        backgroundColor: colors.backgroundLight,
        height: '120px',
        padding: '10px 10px',
        zIndex: 10,
        position: 'relative',
        borderBottom: `12px solid ${colors.white}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center'
      }}>
        <Text style={{
          color: colors.white,
          fontSize: fontSize.xxxl,
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Choose your Plants
        </Text>
      </Box>

      {/* Navigation Bar */}
      <Group justify="space-between" align="center" style={{ 
				padding: '2.5vh 3vw',
				zIndex: 10,
				position: 'relative',
				height: '10.5vh',
				flexShrink: 0,
			}}>
        <Button
          variant="filled"
          onClick={handleBack}
          styles={{
            root: {
              backgroundColor: colors.buttonColor,
              color: colors.white,
              border: 'none',
              borderRadius: '8px',
              padding: '8px 20px',
              fontWeight: 'bold',
              minHeight: '44px',
              fontSize: fontSize.lg,
            }
          }}
        >
          Back
        </Button>
        
        {user.hasActiveSubscription ? (
          <Button
            variant="filled"
            onClick={handleNext}
            styles={{
              root: {
                backgroundColor: colors.buttonColor,
                color: colors.white,
                border: 'none',
                borderRadius: '8px',
                padding: '8px 20px',
                fontWeight: 'bold',
                minHeight: '44px',
                fontSize: fontSize.lg,
              }
            }}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubscribe}
            styles={{
              root: {
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: 'bold',
                whiteSpace: 'normal',
                maxWidth: '200px',
                textAlign: 'center',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }
            }}
          >
            Subscribe to see plant info
          </Button>
        )}
      </Group>

      {/* Main Content Area - PreviewSVG Background */}
      <Box style={{ 
					flex: 1,
					minHeight: 0,
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					paddingBottom: '25vh' // Space for the fixed bottom panel
				}}>
        {/* PreviewSVG fills the space as background */}
        <Box style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <ChoosePlantPreviewSVG styleOverride={{ width: '100vw' }} />
        </Box>

        {/* Plant Information Panel - Right beneath the preview */}
        <Box style={{
          paddingLeft: '15px',
          paddingRight: '15px',
          zIndex: 100
        }}>
          <ChoosePlantSideWindowMobile
            selectedPlantId={selectedPlantId}
            selectedPlantData={plantData.find((plant) => plant.ID === selectedPlantId)}
            onCategoryChange={(category) => setCurrentPlantCategory(category)}
          />
        </Box>
      </Box>

      {/* Plant List Panel - Fixed at bottom */}
      <Box style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}>
        <PlantListMobile
          plants={filteredPlants}
          plantCategories={plantCategories}
          selectedPlantId={selectedPlantId}
          currentPlantCategory={currentPlantCategory}
          onSelect={(plantId, category, quantity) =>
            handleSelectPlant(plantId, category, quantity)
          }
        />
      </Box>
    </Box>
  );
};

ChoosePlantsMobile.propTypes = {
  gardenBedId: PropTypes.string,
};

export default ChoosePlantsMobile;
